import { find, first, last } from '../utils/collection.utils';
import { getDateAndHour } from '../utils/date.utils';
import { hoursToMillis } from '../utils/time.utils';
import { ManufacturingOrder } from './manufacturing-order';
import { Material } from './material';
import { OperationProcess } from './operation-process';
import { Path } from './path';
import { SchedulerTask } from './scheduler-task';

export class SchedulerTaskManufacturingOrder {
  constructor(
    public manufacturingOrder: ManufacturingOrder,
    public schedulerTask: SchedulerTask,
    public total: number
  ) {}
  private readonly MATERIAL_WITH_WEIGHT = 5;

  get schedulerTaskHash() {
    return this.schedulerTask ? this.schedulerTask.hash : '-';
  }

  get identifier() {
    return this.manufacturingOrder
      ? `${this.manufacturingOrder.code} - ${this.propertyProjectName}`
      : null;
  }

  get propertyPeriod() {
    return this.schedulerTask
      ? getDateAndHour(this.schedulerTask.startDate) +
          '<br/>' +
          getDateAndHour(this.schedulerTask.endDate)
      : null;
  }

  get propertyIdentifier() {
    return this.schedulerTask ? this.schedulerTask.identifier : null;
  }

  get propertyCode() {
    return this.manufacturingOrder ? this.manufacturingOrder.code : null;
  }

  get propertyQuantity() {
    return this.manufacturingOrder.materials
      ? this.manufacturingOrder.materials[0].pieceQuantity
      : null;
  }

  get propertyMaterialCode() {
    return this.manufacturingOrder.materials
      ? find(
          (m: Material) => m.orderNumber === this.MATERIAL_WITH_WEIGHT,
          this.manufacturingOrder.materials
        ).code
      : null;
  }

  get propertyOFQuantity() {
    return this.manufacturingOrder ? this.manufacturingOrder.quantity : null;
  }

  get startDate() {
    return this.schedulerTask ? this.schedulerTask.startDate : 0;
  }

  get endDate() {
    return this.schedulerTask ? this.schedulerTask.endDate : null;
  }

  get startTime() {
    return this.schedulerTask && this.schedulerTask.startDate
      ? this.schedulerTask.startDate
      : 0;
  }
  get endTime() {
    return this.schedulerTask && this.schedulerTask.endDate
      ? this.schedulerTask.endDate
      : 0;
  }

  get propertyProjectName() {
    return this.schedulerTask.schedulerTaskPrograms &&
      this.schedulerTask.schedulerTaskPrograms.length
      ? `${this.schedulerTask.schedulerTaskPrograms[0].print.operationProcess.operationCode}`
      : '-';
  }

  get propertyArticleNumber() {
    return this.manufacturingOrder.articleVersion.article.articleNumber
      ? this.manufacturingOrder.articleVersion.article.articleNumber
      : null;
  }

  get propertyArticleDesc() {
    return this.manufacturingOrder.articleVersion.article.description
      ? this.manufacturingOrder.articleVersion.article.description
      : null;
  }

  get deliveryDate() {
    return this.manufacturingOrder
      ? this.manufacturingOrder.deliveryDate
      : null;
  }

  get propertyMaterial() {
    return this.manufacturingOrder.materials
      ? find(
          (m: Material) => m.orderNumber === this.MATERIAL_WITH_WEIGHT,
          this.manufacturingOrder.materials
        ).denomination
      : null;
  }

  get taskStatus() {
    return this.manufacturingOrder.mpReady;
  }

  get propertyStatus() {
    if (this.schedulerTask.finished) {
      return 'dashboard.scheduler.table.status.finished';
    } else if (this.schedulerTask.created) {
      return 'dashboard.scheduler.table.status.created';
    } else if (this.schedulerTask.preparing) {
      return 'dashboard.scheduler.table.status.preparing';
    } else if (this.schedulerTask.ready) {
      return 'dashboard.scheduler.table.status.ready';
    } else if (this.schedulerTask.running) {
      return 'dashboard.scheduler.table.status.running';
    }
  }

  get getObservations() {
    return this.manufacturingOrder
      ? this.manufacturingOrder.observations
      : null;
  }

  get getPlannerObservations() {
    return this.manufacturingOrder
      ? this.manufacturingOrder.plannerObservations
      : null;
  }

  get propertyRemoveStatus() {
    if (!this.schedulerTask.ready) {
      return 'icon-column hidden-icon';
    } else {
      return 'icon-column text-center';
    }
  }

  get propertyRunning() {
    return this.schedulerTask.running && !this.schedulerTask.finished;
  }

  get propertyPending() {
    return this.schedulerTask.pendingProgramDefinition;
  }

  get propertyPendingMaterial() {
    return !this.manufacturingOrder.mpReady;
  }

  get propertyBlocked() {
    return this.schedulerTask.blocked;
  }

  // OF bloqueada desde Proyecto OF (no confundir con el candado de la tarea).
  get propertyManufacturingOrderBlocked() {
    return !!this.manufacturingOrder && !!this.manufacturingOrder.blocked;
  }

  get propertySituation() {
    return this.manufacturingOrder ? this.manufacturingOrder.situation : null;
  }

  get propertyPPH() {
    return this.schedulerTask.schedulerTaskPrograms &&
      this.schedulerTask.schedulerTaskPrograms.length
      ? this.schedulerTask.schedulerTaskPrograms[0].print.operationProcess
          .prevExecutionTime
      : '-';
  }

  get propertyPDF() {
    return this.manufacturingOrder.existsPDF
      ? 'dashboard.manufacturingOrderArticle.viewer.list.tabs.information.plane'
      : '';
  }

  get propertyToolTable() {
    return this.schedulerTask.toolTable
      ? 'dashboard.manufacturingOrderArticle.viewer.list.tabs.information.toolTable'
      : '';
  }

  get propertyPrepTime() {
    return this.schedulerTask.schedulerTaskPrograms &&
      this.schedulerTask.schedulerTaskPrograms.length
      ? hoursToMillis(
          first(this.schedulerTask.schedulerTaskPrograms).print.operationProcess
            .prevPreparationTime
        )
      : '-';
  }

  get latheDate() {
    return this.manufacturingOrder.latheDate
      ? this.manufacturingOrder.latheDate
      : null;
  }

  get propertyTotalTime() {
    return this.total ? this.total : '-';
  }

  get propertyOperationPathProgram() {
    if (this.schedulerTask.schedulerTaskPrograms) {
      const taskProgram = first(this.schedulerTask.schedulerTaskPrograms);
      if (taskProgram.programReference) {
        return taskProgram.programReference;
      } else {
        const operation: OperationProcess = find(
          (o) =>
            o.operationCode ===
            taskProgram.print.operationProcess.operationCode,
          this.manufacturingOrder.articleVersion.operations
        );
        if (operation) {
          const path: Path = find((p: Path) => {
            return p.model.hash === this.schedulerTask.model;
          }, operation.paths);
          if (path && path.route) {
            return last(path.route.split('/'));
          }
        }
      }
    }
    return '';
  }

  get hasMaterialReady() {
    return this.manufacturingOrder && this.manufacturingOrder.hasMaterialReady
      ? this.manufacturingOrder.hasMaterialReady
      : false;
  }

  get hasMaterialStock() {
    return this.manufacturingOrder && this.manufacturingOrder.hasMaterialInStock
      ? this.manufacturingOrder.hasMaterialInStock
      : false;
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/models/scheduler-task-manufacturing-order.ts